import { utcToZonedTime } from 'date-fns-tz';
import { DateTime } from 'luxon';

/**
   * Calculates the minimum time offset in milliseconds as the number of milliseconds
   * until the next n-minute interval of the hour at least n minutes from now.
   * For example, if minOffsetInMinutes is 5 and the startTime is 9:03,
   * the next available time is 9:10, so the minimum time offset is 7 * 60 * 1000.
   *
   * @param {Date} startTime - The start time.
   * @param {number} minOffsetInMinutes - The minimum offset in minutes.
   * @returns {number} The minimum time offset in milliseconds.
   */
export const calculateMinTimeOffsetInMilliseconds = (startTime, minOffsetInMinutes) => {
  const currentMinute = startTime.getMinutes();
  // Calculate the number of minutes until the next n-minute mark of the hour
  const minutesUntilNextMark = (
    minOffsetInMinutes - (currentMinute % minOffsetInMinutes) + minOffsetInMinutes
  );
  // Determine the final offset based on the minimum offset and the next mark
  const finalOffset = (
    minutesUntilNextMark > minOffsetInMinutes ? minutesUntilNextMark : minOffsetInMinutes
  );
  // Calculate the number of milliseconds until the next interval
  const minTimeOffsetInMilliseconds = finalOffset * 60 * 1000;

  return minTimeOffsetInMilliseconds;
};

/**
 * Converts a string timestamp into a luxon DateTime object
 * @param {String} timeStamp
 * @returns DateTime
 */
export const convertTimeStampToEtDateTime = (timeStamp) => {
  if (!timeStamp) return null;
  // Attempt to parse timestamp from backend
  let etDateTime = DateTime.fromISO(timeStamp, { zone: 'America/New_York' });
  if (!etDateTime.isValid) {
    // If publishDate is not valid, parse under the assumption timestamp comes from Flatpickr
    etDateTime = DateTime.fromFormat(timeStamp, 'y-LL-dd HH:mm', { zone: 'America/New_York' });
  }
  return etDateTime;
};

/**
 * Return now, but in the 'America/New_York' instead of the local tz.
 * This returns a Date that, if printed, displays the time in
 * 'America/New_York', but it's actually in the *local* time zone
 *
 * Ex:
 * Say I am on the west coast and the current local time is:
 * Mon Oct 23 2023 11:45:34 GMT-0700 (Pacific Daylight Time)
 *
 * This is equivalent to:
 * Mon Oct 23 2023 14:45:34 GMT-0400 (Eastern Daylight Time)
 *
 * The date object returned by this function would be:
 * Mon Oct 23 2023 14:45:34 GMT-0700 (Pacific Daylight Time)
 *
 * Unintuitive but necessary to work with the date objects returned by FlatPickr
*/
export const getNowInEasternTimeLocal = () => {
  return utcToZonedTime(Date.now(), 'America/New_York');
};

/**
 * Converts the local time to Eastern Time and returns it as a DateTime object
 * @returns DateTime
 */
export const getNowDateTimeInET = () => {
  return DateTime.local().setZone('America/New_York');
};

/**
 * Converts DateTime object into human readable string using US English
 * final output like: "October 25, 2023 at 5:00 PM EDT"
 * @param {DateTime} dateTime A DateTime object
 * @returns {String} Human-readable string of the datetime
 */
export const convertDateTimeToUSLocaleStr = (dateTime) => {
  return dateTime.setLocale('en-US').toLocaleString(DateTime.DATETIME_FULL);
};

/**
 * Converts a given date and time to a custom US locale string format.
 * The format of the output string is "MM / DD / YYYY, HH:MM AM/PM TIMEZONE".
 *
 * @param {string|number|Date} dateTime - The date and time to convert.
 * @returns {string} The formatted date and time string.
 */
export const convertDateTimeToUSLocaleStrCustom = (dateTime) => {
  // final output like: "10 / 25 / 2023, 5:00 PM EDT"
  // Create a date object
  const date = new Date(dateTime);
  const dateInET = utcToZonedTime(date, 'America/New_York');

  // Extract and format date parts
  const month = (dateInET.getMonth() + 1).toString().padStart(2, '0');
  const day = dateInET.getDate().toString().padStart(2, '0');
  const year = dateInET.getFullYear();

  // Extract and format time parts
  let hours = dateInET.getHours();
  const minutes = dateInET.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours %= 12;
  hours = hours ? hours.toString().padStart(2, '0') : '12'; // Convert 0 to 12 for 12 AM
  // convert to eastern time

  // Construct the formatted date string
  const formattedDate = `${month} / ${day} / ${year}, ${hours}:${minutes} ${ampm} ET`;
  return formattedDate;
};

/**
 * Returns today's date set to midnight.
 * @returns {Date} Today's date with hours, minutes, seconds, and ms set to 0
*/
export const getTodayAtLocalMidnight = () => {
  return new Date().setHours(0, 0, 0, 0);
};

/**
 * Generates the appropriate success message for a published press release
 * based on whether it has a publish date and if it's scheduled for the future.
 *
 * @param {string|null} publishDateISO - The publish date as an ISO string, or null
 * for immediate publish
 * @returns {string} The success message to display
 */
export const getPublishSuccessMessage = (publishDateISO) => {
  if (!publishDateISO) {
    return 'The press release has been updated and published.';
  }

  const publishDateTime = convertTimeStampToEtDateTime(publishDateISO);
  const nowDateTimeInET = getNowDateTimeInET();

  if (publishDateTime <= nowDateTimeInET) {
    // Publish date is now or in the past, published immediately
    return 'The press release has been updated and published.';
  }

  // Scheduled for future publication
  const formattedDate = convertDateTimeToUSLocaleStrCustom(publishDateTime);
  return `The press release has been updated and will be published on ${formattedDate}`;
};

/**
 * Checks if a press release is scheduled for future publication.
 *
 * @param {string|null} publishDateISO - The publish date as an ISO string, or null
 * @returns {boolean} True if the press release is scheduled for the future
 */
export const isFuturePublish = (publishDateISO) => {
  if (!publishDateISO) return false;

  const publishDateTime = convertTimeStampToEtDateTime(publishDateISO);
  const nowDateTimeInET = getNowDateTimeInET();

  return publishDateTime > nowDateTimeInET;
};

/**
 * Gets formatted publish information for scheduled press releases.
 *
 * @param {string|null} publishDateISO - The publish date as an ISO string, or null
 * @returns {object|null} Object with formatted date and isFuture flag, or null if not applicable
 */
export const getScheduledPublishInfo = (publishDateISO) => {
  if (!publishDateISO) return null;

  const publishDateTime = convertTimeStampToEtDateTime(publishDateISO);
  const nowDateTimeInET = getNowDateTimeInET();
  const isFuture = publishDateTime > nowDateTimeInET;

  if (!isFuture) return null;

  return {
    formattedDate: convertDateTimeToUSLocaleStrCustom(publishDateTime),
    isFuture: true,
  };
};
